import React from 'react';
import { find, get, isEmpty } from 'lodash';

// React view component to render
import { authFlowCallerIdentificationOthers as AuthFlowCallerIdentificationOthersView } from 'ui/views/widgets/authFlowCallerIdentificationOthers';
import { AuthFlowCallerIdentificationOthersService } from 'services/authFlowCallerIdentificationOthers';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { getCallerTypeFromName } from 'resolvers/callStateEntities';

/**
 * @type { class }
 * @desc A React container/smart component to render a React presentational/dumb component ( view ).
 * @example
 * import { AuthFlowCallerIdentificationOthers } from 'ui/containers/AuthFlowCallerIdentificationOthers';
 *
 * // render
 * <AuthFlowCallerIdentificationOthers propName='propValue'/>
 */
export class AuthFlowCallerIdentificationOthers extends React.Component {

	/**
	 * @param { object } props - props
	 */
	constructor( props ) {
		super( props );

		// bind methods to instance
		this.__bind();

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			mode: 'editable', // could be `readonly` or `editable`
			othersInfo: {}
		};

		// create service instance
		this.authFlowCallerIdentificationOthersService = new AuthFlowCallerIdentificationOthersService();

		// start data fetch
		this.fetchComponentData();
	}

	// bind methods and events to instance
	__bind() {
		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
		this.handleFormSubmit = this.handleFormSubmit.bind( this );
	}

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'AuthFlowCallerIdentificationOthers.fetchComponentData()' );

		// cancel ongoing request
		this.authFlowCallerIdentificationOthersService.cancel();

		// fetch component data
		const {
			status,
			data,
		} = await this.authFlowCallerIdentificationOthersService.fetchComponentData(
			{
				url: this.props.widget.url,
			}
		);

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
	}

	/**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true ) {
		log.debug( 'AuthFlowCallerIdentificationOthers.handleToggleEditMode() ' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
		} );
	}

	/**
	 * @desc Handle form submit.
	 */
	async handleFormSubmit( { title, firstName, lastName } = {} ) {
		log.debug( 'AuthFlowCallerIdentificationOthers.handleFormSubmit()' );

		// get save item action
		const saveAction = find(
			this.state.componentData.sections.callerDetails.actions,
			{ name: 'save' }
		);

		// cancel ongoing request
		this.authFlowCallerIdentificationOthersService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const {
			status,
			data
		} = await this.authFlowCallerIdentificationOthersService.addUpdateItem( {
			url: saveAction.url,
			data: {
				operation: isEmpty( this.state.othersInfo )
					? CRUD_OPERATION.CREATE
					: CRUD_OPERATION.UPDATE,
				title,
				firstName,
				lastName
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				mode: 'readonly',
				othersInfo: data,
			} );
		} else {
			errorOverlay.open(); // show error
		}
	}

	/*********************************************/

	/**
	 * @desc When component unMounts, cleanup resources.
	 */
	componentWillUnmount() {
		this.authFlowCallerIdentificationOthersService.cancel(); // cancel ongoing requests
	}

	/*********************************************/

	/**
	 * @desc Render a React view component
	 */
	render() {

		// getting caller type value
		const callerType = getCallerTypeFromName(
			this.props.widget.value.callerType
		);

		return (
			<AuthFlowCallerIdentificationOthersView
				className={ this.props.className }
				isFetching={ this.state.isFetching }
				networkStatus={ this.state.networkStatus }
				hasCallStarted={ !isEmpty( this.props.callState ) }
				componentData={ get( this.state, 'componentData', {} ) }
				onFormSubmit={ this.handleFormSubmit }
				onToggleEditMode={ this.handleToggleEditMode }
				mode={ this.state.mode }
				callerType={ callerType }
				othersInfo={ this.state.othersInfo }
			/>
		);
	}
 }

// set display name
AuthFlowCallerIdentificationOthers.displayName =
	'AuthFlowCallerIdentificationOthers';

// set default props
AuthFlowCallerIdentificationOthers.defaultProps = {};

// prop types
AuthFlowCallerIdentificationOthers.propTypes = {};
